import posthog from 'posthog-js';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import packageJSON from '../../../../../package.json';
import Button from '../../../../components/Button';
import FeePaymentDisclaimer from '../../../../components/FeePaymentDisclaimer';
import PageHeader from '../../../../components/PageHeader';
import AppConfig from '../../../../config';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import RequestService from '../../../../services/RequestService';
import TypesService from '../../../../services/TypesService';
import { appSlice, sendSlice } from '../../../../store/slices';

import { Margin } from '../../../../components/classNames';
import { TopUpPurpose } from '../../../TopUpCard/components/TopUpPurpose/TopUpPurpose';
import './SendConfirm.scss';

const { routes } = AppConfig;

const CREDIT_CURRENCY = packageJSON?.creditCurrency ?? 'Credits';
const BlockchainsById = TypesService.BlockchainsById;
const CurrenciesById = TypesService.CurrenciesById;
const USDT_TRC20_ID = 3;
const CREDIT_CURRENCY_ID = 8;

const SendConfirm = () => {
	const send = useSelector(store => store.send);
	const asset = useSelector(store => store.send.asset) || {};
	const account = useSelector(store => store.send.account) || {};
	const recipient = useSelector(store => store.send.recipient);
	const amount = useSelector(store => store.send.amount);
	const comment = useSelector(store => store.send.comment);
	const withdrawalId = useSelector(store => store.send.withdrawalId);
	const feeData = useSelector(store => store.send.feeData);
	const withdrawalCardId = useSelector(store => store.send.withdrawalCardId);
	const approvalFiles = useSelector(store => store.send.approvalFiles);

	const currencyData = CurrenciesById[asset.Id];
	const currencyBlockchainData = BlockchainsById[currencyData.BlockchainId];

	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const confirm = () => {
		posthog.capture('confirm_clicked');

		const requestData = { Id: withdrawalId };

		const resolveFunc = response => {
			if (response?.Status === 'Success') {
				HelperService.showNotification({
					type: 'success',
					title: t('Successful'),
					description: t('The transaction has been sent.'),
				});
				dispatch(sendSlice.actions.resetState());
				if (withdrawalCardId) {
					navigate({ pathname: routes.mainPage, hash: withdrawalCardId });
				} else {
					const sendStartRouteParams = {
						pathname: routes.mainPage,
						...location.state?.sendStartRouteParams,
					};

					navigate(sendStartRouteParams);
				}
			} else {
				navigate(-1);
				RequestService.BaseThenFunc(response);
			}
			dispatch(appSlice.actions.setPinConfirm());
		};

		if (feeData.CurrencyId === USDT_TRC20_ID) {
			const requestMethod = async data => {
				try {
					const timerId = setTimeout(() => {
						if (withdrawalCardId) {
							navigate({ pathname: routes.mainPage, hash: withdrawalCardId });
						} else {
							const sendStartRouteParams = {
								pathname: routes.mainPage,
								...location.state?.sendStartRouteParams,
							};
							navigate(sendStartRouteParams);
						}
						HelperService.showNotification({
							type: 'success',
							title: t('Your payment is being processed.'),
							description: t(
								'Processing may take some time and involve multiple technical transactions, all of which will be recorded in the transaction history.'
							),
						});
					}, 3000);

					const response = await RequestService.WithdrawalConfirm(data);
					clearTimeout(timerId);
					return response;
				} catch (error) {
					navigate(-1);
					RequestService.BaseErrorFunc(error);
				}
			};

			HelperService.confirmPin({
				requestMethod,
				requestData,
				resolveFunc,
				rejectFunt: () => {},
			});
		} else {
			HelperService.confirmPin({
				requestMethod: RequestService.WithdrawalConfirm,
				requestData,
				resolveFunc,
			});
		}
	};

	return (
		<div className='SendConfirm'>
			<PageHeader
				header={'Confirm transaction'}
				icon={currencyData.Name}
				iconLabel={
					currencyData.CompanyIcon !== currencyBlockchainData.Icon
						? currencyBlockchainData.Icon
						: null
				}
			/>
			<div className='SendConfirm__body'>
				<div>
					<div className='SendConfirm__amount-block FormBlock'>
						<div className='SendConfirm__amount'>
							<div className='SendConfirm__amount-label'>
								{t('Amount to send')}
							</div>
							<div className='SendConfirm__amount-value'>
								{HelperService.FormatCrypto(
									amount,
									currencyData.Precision,
									'floor'
								)}{' '}
								{currencyData.Name}
							</div>
						</div>
						<div className='SendConfirm__fee'>
							<div className='SendConfirm__fee-label'>
								{t('Network fee_v2')}
							</div>
							<div className='SendConfirm__fee-value'>
								{HelperService.FormatCrypto(
									feeData.Fee,
									CurrenciesById[feeData.CurrencyId].Precision
								)}{' '}
								{feeData.CurrencyId === CREDIT_CURRENCY_ID
									? CREDIT_CURRENCY
									: CurrenciesById[feeData.CurrencyId].Name}
							</div>
						</div>
					</div>
					<FeePaymentDisclaimer className={Margin.TopSmall} />
					<div className='SendConfirm__addresses-block FormBlock Margin-T-M'>
						<div className='SendConfirm__addresses-from'>
							<div className='SendConfirm__from-label'>{t('From')}</div>
							<div className='SendConfirm__from-address'>{account.Address}</div>
						</div>
						<div className='SendConfirm__addresses-to'>
							<div className='SendConfirm__to-label'>{t('To')}</div>
							<div className='SendConfirm__to-address'>
								{recipient.Address || recipient}
							</div>
						</div>
						<div className='SendConfirm__addresses-network'>
							<div className='SendConfirm__network-label'>{t('Network')}</div>
							<div className='SendConfirm__network-name'>
								{currencyBlockchainData.Name}
							</div>
						</div>
					</div>

					<TopUpPurpose comment={comment} approvalFiles={approvalFiles} />
				</div>
				<Button
					className='SendConfirm__confirm-button'
					onClick={confirm}
					iconBefore={'VIEWFINDER_KEY'}
				>
					{t('Confirm')}
				</Button>
			</div>
		</div>
	);
};

export default SendConfirm;
